/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import { isExplore } from 'util/AssetUtil';

const VIDA_MAP_ANIM_TIMEOUT_MILLIS = 10000; //10 seconds

const DOM_CLASSES_MAP = {
	relationship: '.relationshipViewWrapper:visible',
	card: '.explore-content:visible'
};

class ShareActionHandler {

	/**
	 * @param {options} set of initial properties
	 */
	constructor(options) {
		this.vidaMapAnimTimeout = options && options.vidaMapAnimTimeout || VIDA_MAP_ANIM_TIMEOUT_MILLIS;
	}

	execute(context) {
		if (context.urlMap.objRef) {
			const share  = {
				perspective: 'explore',
				objRef: context.urlMap.objRef
			};
			const view = context.glassContext.appController.getCurrentContentView();
			if (view.perspective === 'explore' && typeof(view.boardController.layoutController.getCurrentSubViewId) === 'function') {
				share.subView = view.boardController.layoutController.getCurrentSubViewId();
			}
			return share;
		} else {
			throw new Error('Unable to resolve share context. Missing objRef.');
		}
	}

	getShareableItems(context) {
		const items = [];
		const view = context.glassContext.appController.getCurrentContentView();
		let captureReadyPromise = Promise.resolve();
		if (view.perspective === 'explore') {
			const exploreApi = view.getExploreApi();
			captureReadyPromise = this._waitForViewLoaded(exploreApi).then(() => {
				const $content = this._getCapturedElement(view, exploreApi);
				items.push({
					el: $content.length === 1 ? $content.get(0) : view.$el.get(0) /* should never get here but just in case... */,
					label: view.getTitle()
				});
			});
		}

		return captureReadyPromise
			.then(() => this.reRenderMaps(view))
			.then(() => Promise.resolve(items));
	}

	_waitForViewLoaded(exploreApi) {
		return exploreApi.isStartingPointsDisplayed()
			? exploreApi.getStartingPointsAPI().whenRelationshipDataReady()
			: Promise.resolve();

	}

	_getCapturedElement(view, exploreApi) {
		const className = exploreApi.isStartingPointsDisplayed() ? 'relationship' : 'card';
		return  view.$el.find(DOM_CLASSES_MAP[className]);
	}


	/**
	 * Re-render map widgets for screen capture. Allow sub-classes to access.
	 * @protected
	 * @param {Object} view Content View object of the explore perspective
	 * @return {Promise}
	 */
	reRenderMaps (view){
		const TILEDMAP = 'com.ibm.vis.rave2bundletiledmap';
		if(!view.boardController || !view.boardController.layoutController){
			return Promise.resolve();
		}
		const layoutController = view.boardController.layoutController;
		const selectedWidgets = layoutController.getSelectedWidgets();
		let promises = [];
		if(selectedWidgets && selectedWidgets.length > 0){
			promises = selectedWidgets.map(function(widget) {
				//Rerender Map only
				if(layoutController.getLayoutWidgetModel(widget.id).visId === TILEDMAP){
					const canvas = layoutController.dashboardApi.getCanvas();
					const widgetApi  = canvas.getWidget(widget.id);
					const visApi = widgetApi.getVisApi();
					return visApi.mapVizRenderForPrint();
				}
			}).filter( entry => entry !== undefined);
		}
		if (promises.length > 0) {
			const delay = (time) => {
				return (result) => new Promise( (resolve) => {
					setTimeout(() => resolve(result), time);
				});
			};
			return Promise.all(promises).then(delay(this.vidaMapAnimTimeout)); //Timeout is needed for the VIDA map animation.
		} else {
			return Promise.resolve({});
		}
	}

	isEnabled(context) {
		return this.isExplore(context);
	}

	isExplore({ glassContext = {}, context } = {}) {
		const {
			appController: {
				currentAppView: {
					perspective
				} = {}
			} = {}
		} = glassContext;

		return perspective === 'explore' || isExplore(context);
	}

	getInstrumentation(context) {
		return context.glassContext.appController
			.getCurrentContentView()
			.getDashboardApi()
			.getFeature('segment')
			.getInfo({ type : 'Shared Object' });
	}
}

export default ShareActionHandler;



// WEBPACK FOOTER //
// ./src/js/glass/controllers/ShareActionHandler.js